<template>
  <div class="choose-free-plan-section">
    <template v-if="canCancelPaidPlan">
      <h4
        class="choose-free-plan-section__title"
        v-html="$tc('CancelPaidPlan.EditPage.changeFreePlan')"
      />

      <toggle-radio-card-list-section>
        <toggle-radio-card-parts
          :radioValue="freePlan.planGroupId"
          :currentValue="selectedPlanGroupId"
        >
          <plan-card-parts :plan="freePlan" :procedureScreen="procedureScreen" />
        </toggle-radio-card-parts>
      </toggle-radio-card-list-section>

      <ul class="choose-free-plan-section__attention" v-html="attention" />
    </template>
    <!-- 解約不可能な場合 -->
    <template v-else>
      <current-plan-card-section
        :sectionTitle="futurePlanTitle"
        :plan="futurePlan"
        :contractTerm="scheduledContractTerm"
        :isConfirmed="true"
      />
      <ul
        class="choose-free-plan-section__attention choose-free-plan-section__future"
        v-html="attention"
      />
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, ref } from '@vue/composition-api'
import ToggleRadioCardListSection from '@/components/common/ToggleRadioCard/ToggleRadioCardListSection.vue'
import ToggleRadioCardParts from '@/components/common/ToggleRadioCard/ToggleRadioCardParts.vue'
import PlanCardParts from '@/components/SignupPage/common/PlanCardParts.vue'
import CurrentPlanCardSection from '@/components/SignupPage/common/CurrentPlanCardSection.vue'
import i18n from '@/locales/I18n'
import ContractPlanDocument, {
  FreePlanGroupType,
} from '@/store/stores/collectionModule/documents/plan/ContractPlanDocument'
import {
  ContractTermType,
  ProcedureScreenType,
} from '@/store/stores/collectionModule/documents/GeneralTypes'
import useDisplayDependingOnLang from '@/components/hook/useDisplayDependingOnLang'

/**
 * 有料会員解約: 使用できなくなる機能 セクション
 */
export default defineComponent({
  name: 'ChooseFreePlanSection',
  components: {
    ToggleRadioCardListSection,
    ToggleRadioCardParts,
    PlanCardParts,
    CurrentPlanCardSection,
  },
  props: {
    /**
     * 無料プラン
     */
    freePlan: {
      type: Object as PropType<ContractPlanDocument>,
      default: () => ({}),
    },
    /**
     * 現在のプラン
     */
    currentPlan: {
      type: Object as PropType<ContractPlanDocument>,
      default: () => ({}),
    },
    /**
     * 将来のプラン
     */
    futurePlan: {
      type: Object as PropType<ContractPlanDocument>,
      default: () => ({}),
    },
    /**
     * 選択中のプラングループID（プラン）
     */
    selectedPlanGroupId: {
      type: String as PropType<FreePlanGroupType>,
      required: true,
    },
    /**
     * 契約中のプランの契約期間
     */
    contractTerm: {
      type: Object as PropType<ContractTermType>,
      default: () => ({}),
    },
    /**
     * 将来のプランの契約期間
     */
    scheduledContractTerm: {
      type: Object as PropType<ContractTermType>,
      default: () => ({}),
    },
    /**
     * 有料プランを解約できるかどうか
     */
    canCancelPaidPlan: {
      type: Boolean,
      default: false,
    },
    /**
     * どの手続き画面か
     */
    procedureScreen: {
      type: String as PropType<ProcedureScreenType>,
      default: null,
    },
  },
  setup(props) {
    const { getDisplayDate } = useDisplayDependingOnLang()

    /** 注記 */
    const attention = ref('')

    /** 無料プランに切り替わる日付 */
    const endDate = props.contractTerm.contractEndDate as number
    const freePlanStartDate = getDisplayDate(endDate + 1)

    /** 注記を設定 */
    const setAttention = () => {
      if (!props.futurePlan || props.canCancelPaidPlan) {
        // 有料プランの解約が可能な場合
        attention.value = i18n
          .t('CancelPaidPlan.EditPage.changeFreePlanAttention', {
            date: freePlanStartDate,
          })
          .toString()
        return
      }

      if (props.futurePlan.planGroupId === 'freePlan') {
        // 将来のプランが無料プランの場合
        attention.value = i18n
          .t('CancelPaidPlan.EditPage.hasFutureFreePlanAttention', {
            date: freePlanStartDate,
          })
          .toString()
      } else {
        // 将来のプランが無料プラン以外場合
        attention.value = i18n
          .t('CancelPaidPlan.EditPage.hasFuturePaidPlanAttention', {
            date: freePlanStartDate,
          })
          .toString()
      }
    }
    /** 注記を設定する */
    setAttention()

    /** 将来のプランがある場合の見出し */
    const futurePlanTitle = i18n
      .t('MypagePage.plan.futurePlan', {
        date: freePlanStartDate,
      })
      .toString()

    return {
      attention,
      futurePlanTitle,
    }
  },
})
</script>

<style lang="scss" scoped>
.choose-free-plan-section {
  width: 100%;
  padding-bottom: 24px;
  font-family: $fontSignup;

  &__title {
    display: flex;
    flex-flow: row wrap;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 0 0.5em;
    margin-bottom: 24px;
    @include fontSetTextMedium(17);

    text-align: center;
    letter-spacing: 0.04em;
    font-feature-settings: 'palt' on;
  }

  &__attention {
    display: flex;
    flex-flow: column nowrap;
    align-items: flex-start;
    align-content: flex-start;
    justify-items: flex-start;
    justify-content: flex-start;
    width: 100%;
    max-width: 800px;
    margin: 24px auto 0;
    padding: 0 16px;
    text-align: left;
    @include fontSetTextMedium(12);

    color: $black50;
  }

  &__future {
    margin: 0 auto;
    color: $red;
  }
}
</style>

<style lang="scss">
.choose-free-plan-section {
  &__attention {
    li::before {
      display: inline-block;
      content: '*';
    }
  }
}
</style>
